import React, { useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { getCustomerPortalUrl } from '../services/subscriptionService';
import { Card, CardBody, Badge, Button } from './BaseComponents';

/**
 * SubscriptionStatus component displays current subscription information
 * and provides access to Stripe Customer Portal for subscription management
 */
export const SubscriptionStatus: React.FC = () => {
    const { currentUser, hasQuizAccess } = useUser();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!currentUser) return null;

    const handleManageSubscription = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const { url } = await getCustomerPortalUrl();
            window.location.href = url;
        } catch (err: any) {
            console.error('Failed to get customer portal URL:', err);
            setError(err.message || 'Failed to open subscription management');
            setIsLoading(false);
        }
    };

    const getStatusBadge = () => {
        if (currentUser.subscriptionStatus === 'active') {
            return <Badge variant="success">Active Subscription</Badge>;
        }

        // Check if user has active trial
        if (currentUser.trialExpiry && new Date(currentUser.trialExpiry) > new Date()) {
            const daysLeft = Math.ceil((new Date(currentUser.trialExpiry).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
            return <Badge variant="info">Trial Active ({daysLeft} days left)</Badge>;
        }

        return <Badge variant="warning">No Active Subscription</Badge>;
    };

    const getStatusMessage = () => {
        if (currentUser.subscriptionStatus === 'active' && currentUser.subscriptionEndDate) {
            const endDate = new Date(currentUser.subscriptionEndDate).toLocaleDateString();
            return `Your subscription is active and will renew on ${endDate}.`;
        }

        if (currentUser.trialExpiry && new Date(currentUser.trialExpiry) > new Date()) {
            const trialEnd = new Date(currentUser.trialExpiry).toLocaleDateString();
            return `Your free trial expires on ${trialEnd}. Subscribe to continue access.`;
        }

        return 'Subscribe to access all quiz features and track your progress.';
    };

    const shouldShowManageButton = () => {
        return currentUser.subscriptionStatus === 'active' || currentUser.stripeCustomerId;
    };

    return (
        <Card className="border border-neutral-200" noHover={true}>
            <CardBody>
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-medium text-neutral-900">
                                Subscription Status
                            </h3>
                            {getStatusBadge()}
                        </div>
                        <p className="text-neutral-600">
                            {getStatusMessage()}
                        </p>
                        {error && (
                            <p className="text-red-600 text-sm mt-2">
                                {error}
                            </p>
                        )}
                    </div>

                    {shouldShowManageButton() && (
                        <div className="flex flex-col gap-2">
                            <Button
                                variant="outline"
                                onClick={handleManageSubscription}
                                disabled={isLoading}
                                className="whitespace-nowrap"
                            >
                                {isLoading ? 'Opening...' : 'Manage Subscription'}
                            </Button>
                            <p className="text-xs text-neutral-500 text-center">
                                Cancel or update billing
                            </p>
                        </div>
                    )}
                </div>

                {/* Access Status Indicator */}
                <div className="mt-4 pt-4 border-t border-neutral-100">
                    <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${hasQuizAccess() ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className="text-sm text-neutral-600">
                            Quiz Access: {hasQuizAccess() ? 'Enabled' : 'Disabled'}
                        </span>
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};